import { useAuth } from "@/contexts/AuthContext";
import { useFirms } from "@/hooks/useFirms";
import { canChangeFirm } from "@/permissions/firms";
import { FirmsRepository, useRepository } from "@/repositories";
import { usePreferencesStore } from "@/store/preferencesStore";
import "@/styles/Layout.css";
import { getErrorMessage } from "@/utils/errorUtils";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";

import { FirmContextDropdownView } from "./FirmContextDropdownView";

const ALL_ASSIGNED_CASES_VALUE = "all_assigned_cases";
const GROUP_HAS_SUBSCRIPTION = "With subscription";
const GROUP_NO_SUBSCRIPTION = "Without subscription";

const getCommandEmptyLabel = (params: { isLoading: boolean; firmsCount: number }): string => {
  if (params.isLoading) return "Loading...";
  if (params.firmsCount === 0) return "No firms found.";
  return "No results found.";
};

const getDisplayLabel = (params: {
  isSwitchingFirm: boolean;
  isMyCases: boolean;
  selectedValue: string | null;
  firmName: string | null | undefined;
}): string | null => {
  if (params.isSwitchingFirm) return "Switching firm…";
  if (params.isMyCases || params.selectedValue === ALL_ASSIGNED_CASES_VALUE) {
    return "My cases (All Firms)";
  }
  return params.firmName ?? null;
};

const FirmContextDropdown: React.FC = () => {
  const { firmContext, user, isMyCases } = useAuth();
  const { firms: allFirms, isLoading } = useFirms();
  const firmsRepository = useRepository(FirmsRepository);
  const [isSwitchingFirm, setIsSwitchingFirm] = useState<boolean>(false);
  const [pendingFirmName, setPendingFirmName] = useState<string | null>(null);
  const createdFirmIdRef = useRef<string | null>(null);

  // Narrowed once, before any hook, so every hook body below sees a
  // non-null FirmContext without re-deriving the canChangeFirm check.
  const activeFirmContext = firmContext && canChangeFirm(firmContext) ? firmContext : null;

  const pickerItems = useMemo(() => {
    const firmRows = allFirms.map((firm) => ({
      value: firm.firm_id,
      label: firm.name,
      group: firm.has_active_subscription ? GROUP_HAS_SUBSCRIPTION : GROUP_NO_SUBSCRIPTION,
    }));

    if (activeFirmContext?.roleName === "super_analyst") {
      return [
        // Keep pseudo option pinned at top (ungrouped)
        { value: ALL_ASSIGNED_CASES_VALUE, label: "My cases (All Firms)" },
        ...firmRows,
      ];
    }

    return firmRows;
  }, [allFirms, activeFirmContext?.roleName]);

  const selectedValue = isMyCases ? ALL_ASSIGNED_CASES_VALUE : (activeFirmContext?.firmId ?? null);

  const displayLabel = useMemo(
    () =>
      getDisplayLabel({
        isSwitchingFirm,
        isMyCases,
        selectedValue,
        firmName: activeFirmContext?.firmName,
      }),
    [activeFirmContext?.firmName, isMyCases, isSwitchingFirm, selectedValue]
  );

  const commandEmptyLabel = useMemo(
    () => getCommandEmptyLabel({ isLoading, firmsCount: allFirms.length }),
    [allFirms.length, isLoading]
  );

  const handleFirmSwitch = useCallback(
    async (firmId: string): Promise<void> => {
      if (!user || isSwitchingFirm || !activeFirmContext) {
        toast.error("User Context not found or firm is being switched");
        return;
      }

      setIsSwitchingFirm(true);
      try {
        const { setFirmSelectionMade, setSelectedPseudoFirm } = usePreferencesStore.getState();

        setFirmSelectionMade(true);
        if (firmId === ALL_ASSIGNED_CASES_VALUE) {
          setSelectedPseudoFirm(ALL_ASSIGNED_CASES_VALUE);
        } else {
          setSelectedPseudoFirm(null);
          await firmsRepository.assignUserToFirm(firmId, user.id, activeFirmContext.roleName);
        }
        toast.success("Firm assigned successfully");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        toast.error(getErrorMessage(error));
        setIsSwitchingFirm(false);
      }
    },
    [activeFirmContext, firmsRepository, isSwitchingFirm, user]
  );

  const handleCreateFirmRequest = useCallback(
    (label: string): false => {
      if (!user || isSwitchingFirm) {
        toast.error("User Context not found or firm is being switched");
        return false;
      }
      const trimmed = label.trim();
      if (!trimmed) {
        toast.error("Please enter a firm name");
        return false;
      }
      createdFirmIdRef.current = null;
      setPendingFirmName(trimmed);
      return false;
    },
    [isSwitchingFirm, user]
  );

  const handleConfirmCreateFirm = useCallback(async (): Promise<void> => {
    if (!pendingFirmName || !user) return;

    setIsSwitchingFirm(true);
    try {
      const firmId =
        createdFirmIdRef.current ?? (await firmsRepository.createFirm(pendingFirmName));
      createdFirmIdRef.current = firmId;
      await firmsRepository.assignUserToFirm(firmId, user.id, "super_admin");

      const { setFirmSelectionMade, setSelectedPseudoFirm } = usePreferencesStore.getState();
      setFirmSelectionMade(true);
      setSelectedPseudoFirm(null);

      toast.success("Firm created and assigned successfully");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setIsSwitchingFirm(false);
    }
  }, [firmsRepository, pendingFirmName, user]);

  const handleCancelCreateFirm = useCallback((): void => {
    setPendingFirmName(null);
    createdFirmIdRef.current = null;
  }, []);

  // Only show to super_admin, super_analyst
  if (!activeFirmContext) return null;

  const isSuperAdmin = activeFirmContext.roleName === "super_admin";

  return (
    <FirmContextDropdownView
      items={pickerItems}
      value={selectedValue}
      displayLabel={displayLabel}
      isLoading={isLoading || isSwitchingFirm}
      disabled={isSwitchingFirm}
      commandEmptyLabel={commandEmptyLabel}
      isCreateEnabled={isSuperAdmin}
      onChangeFirm={(firmId) => {
        void handleFirmSwitch(firmId);
      }}
      onCreateFirm={handleCreateFirmRequest}
      pendingFirmName={pendingFirmName}
      isCreatingFirm={isSwitchingFirm}
      onConfirmCreateFirm={() => {
        void handleConfirmCreateFirm();
      }}
      onCancelCreateFirm={handleCancelCreateFirm}
      groupOrder={[GROUP_HAS_SUBSCRIPTION, GROUP_NO_SUBSCRIPTION]}
    />
  );
};

export default FirmContextDropdown;
